import type { ConversationMessage } from '@/api/conversation'
import type { ConversationState } from './state'

export function safeCitationIDs(refs: unknown): Array<number> {
  if (typeof refs !== 'object' || refs === null || Array.isArray(refs)) return []
  const values = (refs as Record<string, unknown>)['docIds']
  if (!Array.isArray(values) || values.length > 32) return []
  if (!values.every((value) => typeof value === 'number' && Number.isSafeInteger(value) && value > 0)) return []
  if (new Set(values).size !== values.length) return []
  return [...values] as Array<number>
}

export interface ChatContextSelection {
  answer: ConversationMessage | undefined
  isLatest: boolean
  sourceIds: Array<number>
  briefId: number | undefined
  documents: Array<{ id: number; label: string }>
}

export function selectChatContext(state: ConversationState, selectedMessageId?: number): ChatContextSelection {
  const messages = [...state.olderMessages, ...state.liveMessages]
    .filter((message) => message.author === 'assistant' || message.author === 'computed-facts')
    .sort((a, b) => a.createdAtTsMs - b.createdAtTsMs || a.messageId - b.messageId)
  const latest = messages.at(-1)
  const answer =
    selectedMessageId === undefined ? latest : messages.find((message) => message.messageId === selectedMessageId)
  const citations = answer === undefined ? [] : safeCitationIDs(answer.refs)
  const computed = answer?.author === 'computed-facts' ? (answer.computedResults ?? []) : []
  const sourceIds = [...new Set([...citations, ...computed.flatMap((result) => result.sourceDocumentIds)])]
  const brief = state.briefs.at(-1)
  const documents = new Map<number, { id: number; label: string }>()
  const validBrief = brief !== undefined && !state.invalidatedBriefIds.includes(brief.briefId) ? brief : undefined
  for (const slot of validBrief?.slots ?? []) {
    const references =
      slot.value.type === 'document-reference'
        ? [slot.value.document]
        : slot.value.type === 'document-references'
          ? slot.value.documents
          : []
    for (const reference of references) {
      documents.set(reference.docId, { id: reference.docId, label: reference.fileName.trim() || 'Workspace source' })
    }
  }
  return {
    answer,
    isLatest: selectedMessageId === undefined || answer?.messageId === latest?.messageId,
    sourceIds,
    briefId: validBrief?.briefId,
    documents: [...documents.values()]
  }
}
